import { IMeeting } from 'types/meetings';

import { isDesktopContext } from 'helpers/desktopApp';
import MeetingState from 'helpers/meetingState';
import { isMeetingHappening } from 'helpers/meetingsData';

export const DESKTOP_MEETING_SERVICE = 'desktop';

export const DESKTOP_LIVE_RECORDING_STATE_KEYS = [
  MeetingState.botRecStart,
  MeetingState.botRecPause,
  MeetingState.botRecResume
] as const;

export type MeetingMetadataStates = {
  subject?: string;
  states?: {
    meeting?: {
      key?: string;
      value?: string;
    };
  };
};

export function isDesktopLocalRecordingMeeting(
  meeting?: IMeeting | null
): boolean {
  return (
    meeting?.conferenceData?.service?.toLowerCase() === DESKTOP_MEETING_SERVICE
  );
}

function isLiveDesktopRecordingStateKey(stateKey?: string): boolean {
  return (
    !!stateKey &&
    (DESKTOP_LIVE_RECORDING_STATE_KEYS as readonly string[]).includes(stateKey)
  );
}

/**
 * Metadata websocket/API can lag behind the meeting payload during desktop
 * recording startup. Prefer whichever source already reports live recording.
 */
export function resolveMeetingStatesForDesktopLiveSync(
  baseMeeting?: IMeeting | null,
  meetingMetadata?: MeetingMetadataStates | null
) {
  const metadataStates = meetingMetadata?.states;
  const meetingStates = baseMeeting?.states;

  if (!metadataStates) {
    return meetingStates;
  }
  if (!meetingStates) {
    return metadataStates;
  }

  const metadataKey = metadataStates.meeting?.key;
  const meetingKey = meetingStates.meeting?.key;

  if (isLiveDesktopRecordingStateKey(metadataKey)) {
    return metadataStates;
  }
  if (isLiveDesktopRecordingStateKey(meetingKey)) {
    if (metadataKey === MeetingState.infoMissing || !metadataKey) {
      return meetingStates;
    }

    return metadataStates;
  }

  if (
    metadataKey === MeetingState.infoMissing &&
    meetingKey &&
    meetingKey !== MeetingState.infoMissing
  ) {
    return meetingStates;
  }

  return metadataStates;
}

export function getMeetingStateKey(
  meeting?: IMeeting | null,
  meetingMetadata?: MeetingMetadataStates | null
): string | undefined {
  return resolveMeetingStatesForDesktopLiveSync(meeting, meetingMetadata)
    ?.meeting?.key;
}

export function isDesktopMeetingRecordingLive(
  meeting?: IMeeting | null,
  meetingMetadata?: MeetingMetadataStates | null
): boolean {
  const stateKey = getMeetingStateKey(meeting, meetingMetadata);

  if (!stateKey) {
    return false;
  }

  return isLiveDesktopRecordingStateKey(stateKey);
}

export function shouldRunDesktopMeetingLiveSync(
  meeting?: IMeeting | null,
  meetingMetadata?: MeetingMetadataStates | null
): boolean {
  if (!isDesktopContext() || !isMeetingHappening(meeting)) {
    return false;
  }

  if (isDesktopLocalRecordingMeeting(meeting)) {
    return true;
  }

  const stateKey = getMeetingStateKey(meeting, meetingMetadata);

  return (
    stateKey === MeetingState.infoMissing ||
    isLiveDesktopRecordingStateKey(stateKey)
  );
}

function getMetadataKey(
  meetingMetadata?: MeetingMetadataStates | null
): string | undefined {
  if (!meetingMetadata) {
    return undefined;
  }

  return JSON.stringify([
    meetingMetadata.states?.meeting?.key,
    meetingMetadata.subject
  ]);
}

type MergedMeetingCache = {
  uuid: string | null;
  baseMeeting: IMeeting | null;
  metadataKey: string | undefined;
  stateKey: string | undefined;
  merged: IMeeting | null;
};

let mergedMeetingCache: MergedMeetingCache = {
  uuid: null,
  baseMeeting: null,
  metadataKey: undefined,
  stateKey: undefined,
  merged: null
};

/** @internal test helper */
export function resetMergedMeetingCacheForTests() {
  mergedMeetingCache = {
    uuid: null,
    baseMeeting: null,
    metadataKey: undefined,
    stateKey: undefined,
    merged: null
  };
}

function buildMergedMeetingStates(
  baseMeeting: IMeeting,
  states: NonNullable<ReturnType<typeof resolveMeetingStatesForDesktopLiveSync>>
): IMeeting['states'] {
  if (states === baseMeeting.states || !baseMeeting.states) {
    return (states ?? baseMeeting.states) as IMeeting['states'];
  }

  const resolvedMeeting = states.meeting;

  return {
    ...baseMeeting.states,
    meeting: {
      ...baseMeeting.states.meeting,
      ...(resolvedMeeting?.key ? { key: resolvedMeeting.key } : {}),
      ...(resolvedMeeting?.value !== undefined
        ? { value: resolvedMeeting.value }
        : {})
    }
  };
}

/**
 * Merge websocket metadata onto the meeting without creating a new object on
 * every poll tick when the resolved live recording state and subject are unchanged.
 */
export function mergeMeetingWithLiveMetadata(
  baseMeeting?: IMeeting | null,
  meetingMetadata?: MeetingMetadataStates | null
): IMeeting | null | undefined {
  if (!baseMeeting) {
    mergedMeetingCache = {
      uuid: null,
      baseMeeting: null,
      metadataKey: undefined,
      stateKey: undefined,
      merged: null
    };
    return baseMeeting;
  }

  const states = resolveMeetingStatesForDesktopLiveSync(
    baseMeeting,
    meetingMetadata
  );
  const subject = meetingMetadata?.subject;
  const hasUpdatedSubject =
    typeof subject === 'string' && subject !== baseMeeting.subject;
  if (!states && !hasUpdatedSubject) {
    return baseMeeting;
  }

  const stateKey = states?.meeting?.key;
  const metadataKey = getMetadataKey(meetingMetadata);
  const {
    uuid,
    baseMeeting: cachedBase,
    metadataKey: cachedMetadataKey,
    stateKey: cachedKey,
    merged
  } = mergedMeetingCache;

  if (
    uuid === baseMeeting.uuid &&
    cachedBase === baseMeeting &&
    cachedMetadataKey === metadataKey &&
    cachedKey === stateKey &&
    merged
  ) {
    return merged;
  }

  const nextMerged: IMeeting = {
    ...baseMeeting,
    ...(hasUpdatedSubject ? { subject } : {}),
    ...(states ? { states: buildMergedMeetingStates(baseMeeting, states) } : {})
  };
  mergedMeetingCache = {
    uuid: baseMeeting.uuid,
    baseMeeting,
    metadataKey,
    stateKey,
    merged: nextMerged
  };
  return nextMerged;
}
